import { useEffect, useMemo, useState } from 'react'
import { Text, Container, Link } from '../../components/index.js'
import Subscribe from '../Subscribe/index.js'
import type { FooterNavigationProps } from './types.js'
import { FooterNavigationBlockType } from './types.js'
import { AddIcon, SubtractIcon, ArrowRightIcon, CountryIcon } from './icons/index.js'
import { useMediaQuery } from 'react-responsive'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { withLayout } from '../../shared/Styles.js'

const FooterNavigation = ({
  data: { footer } = {},
  currentCountry,
  event,
  subscribeLoading,
  classNames,
  className,
  moduleIds,
  ...rest
}: FooterNavigationProps) => {
  const [isMobile, setIsMobile] = useState(false)

  const mediaQuery = useMediaQuery({ query: '(max-width: 1439px)' })

  useEffect(() => {
    setIsMobile(mediaQuery)
  }, [mediaQuery])

  const country = useMemo(() => {
    return footer?.blocks?.find((block: any) => block?.blockType === FooterNavigationBlockType.Brand)?.country || {}
  }, [footer])

  return (
    <footer {...rest} className={cn(classNames?.root, className)}>
      <Container
        className={cn(classNames?.child)}
        childClassName={cn('laptop:py-16 desktop:gap-16 flex flex-col gap-8 bg-black py-8')}
      >
        <Text html={footer?.story} className="desktop:text-sm text-xs font-bold text-[#6D6D6F]" />
        {footer?.blocks?.map((block: any) => {
          if (block?.blockType === FooterNavigationBlockType.Signup) {
            return (
              <MarketingConversion
                key={block.id}
                subscribeLoading={subscribeLoading}
                data={block}
                onSubmit={event?.signup}
                id={moduleIds?.marketingConversion}
              />
            )
          }
          if (block?.blockType === FooterNavigationBlockType.Brand) {
            return (
              <FooterBrand
                key={block?.id}
                data={block}
                isMobile={isMobile}
                event={event}
                currentCountry={currentCountry}
                id={moduleIds?.footerBrand}
              />
            )
          }
          if (block?.blockType === FooterNavigationBlockType.Main) {
            return (
              <MainServices
                key={block.id}
                data={{ ...block, country }}
                event={event}
                isMobile={isMobile}
                currentCountry={currentCountry}
                id={moduleIds?.mainServices}
              />
            )
          }
        })}
      </Container>
    </footer>
  )
}

/**
 * MarketingConversion 营销转化模块
 * @param data
 * @param onSubmit signup提交事件
 * @returns
 */
const MarketingConversion = ({
  data,
  onSubmit,
  subscribeLoading,
  id = 'footer-marketing-conversion',
}: {
  data: any
  onSubmit?: (_params: any) => void
  subscribeLoading?: boolean
  id?: string
}) => {
  const { locale = 'us' } = useAiuiContext()

  return (
    <div
      id={id}
      className="desktop:py-16 desktop:gap-16 laptop:px-8 desktop:flex-row rounded-btn flex flex-col gap-8 bg-[#1E2024] px-4 py-8"
    >
      <Subscribe
        subscribeMetadata={data?.signup}
        className="desktop:flex-[744]"
        onSubmit={onSubmit}
        loading={subscribeLoading}
      />
      <div className="desktop:hidden h-px w-full bg-[#3D3E3F]" />
      <div className="desktop:gap-6 laptop:flex-row desktop:flex-[792] flex flex-col gap-4">
        <div className="flex-1">
          <Text html={data?.enjoy?.title} className=" text-xl font-bold leading-[1.2] text-white" />
          <div className="mt-4 flex flex-col gap-2">
            {data?.enjoy?.enjoys?.map((enjoyItem: any) => (
              <div key={enjoyItem?.id} className="flex-start flex gap-2">
                <img
                  src={enjoyItem?.icon?.url}
                  loading="lazy"
                  alt={enjoyItem?.icon?.alt || ''}
                  className="size-5 shrink-0"
                />
                <Text
                  html={enjoyItem?.label}
                  className="desktop:text-base text-sm font-bold leading-[1.2] text-[#B6B6BA]"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="desktop:block hidden w-px bg-[#3D3E3F]" />
        <div className="flex-1">
          <Text html={data?.benefit?.title} className="text-xl font-bold leading-[1.2] text-white" />
          <div className="mt-4 flex flex-col gap-2">
            {data?.benefit?.benefits?.map((benefitItem: any) => (
              <Link
                href={`${getLocalizedPath(benefitItem?.link, locale)}?ref=footer`}
                asChild={!benefitItem?.link}
                key={benefitItem?.id}
                className="no-underline"
              >
                <Text
                  html={benefitItem?.label}
                  className="desktop:text-base text-sm font-bold leading-[1.2] text-[#B6B6BA] hover:text-white"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * MainServices 主导航与服务模块
 * @param services 服务
 * @param mainNav 主导航
 * @param event 事件
 * @param isMobile 是否是移动端
 * @param currentCountry 当前国家
 * @returns
 */
const MainServices = ({
  data,
  event,
  isMobile,
  currentCountry,
  id = 'footer-main-services',
}: {
  data: any
  event: any
  isMobile: boolean
  currentCountry: any
  id?: string
}) => {
  const { locale = 'us' } = useAiuiContext()
  const [mainNavMenuOpen, setMainNavMenuOpen] = useState<boolean>(false)
  const { services, mainNav, country } = data
  const contactSectionId = 'contact-section'

  return (
    <nav id={id} className="flex flex-col gap-8" aria-label="Main navigation">
      <div
        className={cn({
          'desktop:gap-16 flex': services?.download?.title,
        })}
      >
        <div
          className={cn('w-full', {
            'desktop:flex-[1260]': services?.download?.title,
          })}
        >
          <Text html={services?.storeBenefits?.title} className=" text-xl font-bold leading-[1.2] text-white" />
          <div
            className={cn('laptop:flex-row desktop:gap-4 laptop:flex-wrap mt-4 flex flex-col gap-2', {
              'desktop:max-w-[648px]': services?.download?.title,
            })}
          >
            {services?.storeBenefits?.reasons?.map((reasonItem: any) => (
              <div key={reasonItem?.id} className="desktop:min-w-[288px] flex flex-1 shrink-0 items-center gap-2">
                <img
                  src={reasonItem?.icon?.url}
                  loading="lazy"
                  alt={reasonItem?.icon?.alt || ''}
                  className="size-5 shrink-0"
                />
                <Link
                  href={`${getLocalizedPath(reasonItem?.link, locale)}?ref=footer`}
                  asChild={!reasonItem?.link}
                  className="no-underline"
                >
                  <Text
                    html={reasonItem?.label}
                    className="desktop:text-base text-sm font-bold leading-[1.2] text-[#B6B6BA]"
                  />
                </Link>
              </div>
            ))}
          </div>
        </div>
        <DownloadApp
          className={cn({
            'desktop:flex-[404] hidden desktop:block': services?.download?.title,
          })}
          title={services?.download?.title}
          apps={services?.download?.apps}
        />
      </div>
      <div className="desktop:block hidden h-px bg-[#3D3E3F]" aria-hidden="true" />
      <div className="desktop:grid grid-cols-4 gap-4">
        <MainNavMenu title={mainNav?.products?.title} lists={mainNav?.products?.products} />
        <MainNavMenu title={mainNav?.explore?.title} lists={mainNav?.explore?.explores} />
        <MainNavMenu title={mainNav?.support?.title} lists={mainNav?.support?.supports} />
        <div className="desktop:py-0 desktop:border-none border-t border-[#3D3E3F] pt-4">
          <div className="desktop:pb-0 flex items-center justify-between pb-4">
            <Text
              as="p"
              html={mainNav?.contact?.title}
              className="text-xl font-bold leading-[1.2] text-white"
              id={contactSectionId}
            />
            {isMobile && (
              <button
                onClick={() => setMainNavMenuOpen(!mainNavMenuOpen)}
                aria-expanded={mainNavMenuOpen}
                aria-controls={contactSectionId}
                aria-label={mainNavMenuOpen ? 'Collapse contact section' : 'Expand contact section'}
              >
                {mainNavMenuOpen ? <SubtractIcon width={20} height={20} /> : <AddIcon width={20} height={20} />}
              </button>
            )}
          </div>
          {((mainNavMenuOpen && isMobile) || !isMobile) && (
            <div
              className="desktop:mt-4 desktop:mb-0 mb-4 flex flex-col gap-2"
              role="region"
              aria-labelledby={contactSectionId}
            >
              {mainNav?.contact?.contacts?.map((contactItem: any) => (
                <Link
                  key={contactItem?.id}
                  href={contactItem?.link}
                  className="group flex items-start gap-2 no-underline"
                >
                  <img
                    src={contactItem?.icon?.url}
                    loading="lazy"
                    alt={contactItem?.icon?.alt}
                    className="size-5 shrink-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
                  />
                  <Text
                    html={contactItem?.label}
                    className="desktop:text-base text-sm font-bold leading-[1.2] text-[#B6B6BA] transition-colors duration-200 group-hover:text-white"
                  />
                </Link>
              ))}
            </div>
          )}
          <ChangeCountry
            country={currentCountry || country}
            className="desktop:hidden border-b border-[#3D3E3F]"
            isMobile={isMobile}
            onChangeCountry={event?.country}
          />
          <div className="desktop:hidden mt-8">
            <DownloadApp title={services?.download?.title} apps={services?.download?.apps} />
          </div>
          {!!mainNav?.socials?.length && (
            <div className="mt-8 flex items-center gap-2" role="list" aria-label="Social media links">
              {mainNav?.socials?.map((socialItem: any) => (
                <Link
                  href={`${getLocalizedPath(socialItem?.link, locale)}?ref=footer`}
                  key={socialItem?.id}
                  className="group"
                  role="listitem"
                  aria-label={socialItem?.icon?.alt || 'Social media'}
                >
                  <img
                    src={socialItem?.icon?.url}
                    loading="lazy"
                    alt={socialItem?.icon?.alt}
                    className="size-8 shrink-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
                  />
                </Link>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2" role="list" aria-label="Payment methods">
            {mainNav?.payment?.map((paymentItem: any) => (
              <Link
                href={getLocalizedPath(paymentItem?.link, locale)}
                asChild={!paymentItem?.link}
                key={paymentItem?.id}
                role="listitem"
                aria-label={paymentItem?.icon?.alt || 'Payment method'}
              >
                <img
                  src={paymentItem?.icon?.url}
                  loading="lazy"
                  alt={paymentItem?.icon?.alt || ''}
                  className="h-[28px] w-[44px] shrink-0"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  )
}

const FooterBrand = ({
  data,
  isMobile,
  event,
  currentCountry,
  id = 'footer-brand',
}: {
  data: any
  isMobile: boolean
  event: any
  currentCountry: any
  id?: string
}) => {
  const { locale = 'us' } = useAiuiContext()

  return (
    <div id={id} className="flex flex-col gap-4">
      <div className="desktop:items-center desktop:flex desktop:gap-4">
        <div className="laptop:flex-row laptop:items-center desktop:flex-[980] flex flex-col items-start gap-4">
          <Link href={`${getLocalizedPath(data?.brand?.link, locale)}?ref=footer`} asChild={!data?.brand?.link}>
            <Text html={data?.brand?.currentBrand} className="cursor-pointer text-[#B6B6BA] hover:text-white" />
          </Link>
          <div className="laptop:block laptop:mb-[10px] hidden h-[20px] w-px bg-[#B6B6BA]" />
          <div className="desktop:gap-6 flex flex-wrap items-center gap-4">
            {data?.brand?.brands?.map((brandItem: any) => (
              <Link href={`${getLocalizedPath(brandItem?.link, locale)}?ref=footer`} key={brandItem?.id}>
                <Text html={brandItem?.icon} className="text-[#B6B6BA] hover:text-white" />
              </Link>
            ))}
          </div>
        </div>
        <ChangeCountry
          country={currentCountry || data?.country}
          className="desktop:flex-[316] desktop:block hidden"
          isMobile={isMobile}
          onChangeCountry={event?.country}
        />
      </div>
      <div className="h-px bg-[#3D3E3F]" />
      <div className="laptop:flex-row desktop:gap-4 desktop:items-center flex flex-col items-start gap-1">
        <Text
          html={data?.copyright?.label}
          className="desktop:text-base text-xs font-bold leading-[1.4] text-[#6D6D6F]"
        />
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {data?.policy?.policies?.map((policyItem: any) => (
            <Link
              href={`${getLocalizedPath(policyItem?.link, locale)}?ref=footer`}
              key={policyItem?.id}
              className="desktop:text-base text-xs font-bold leading-[1.4] text-[#B6B6BA] no-underline hover:text-white"
            >
              {policyItem?.label}
            </Link>
          ))}
          {data?.policy?.cookies && (
            <button
              data-cc="show-preferencesModal"
              onClick={event?.cookiesSet}
              className="desktop:text-base text-xs font-bold leading-[1.4] text-[#B6B6BA] no-underline hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
              aria-label={`Cookie preferences: ${data?.policy?.cookies}`}
            >
              {data?.policy?.cookies}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const MainNavMenu = ({ title, lists, className }: { title: string; lists: any[]; className?: string }) => {
  const { locale = 'us' } = useAiuiContext()
  const [mainNavMenuOpen, setMainNavMenuOpen] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState(false)

  const mediaQuery = useMediaQuery({ query: '(max-width: 1439px)' })

  useEffect(() => {
    setIsMobile(mediaQuery)
  }, [mediaQuery])

  const sectionId = `nav-section-${title?.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <div className={cn('desktop:py-0 desktop:border-none border-t border-[#3D3E3F] py-4', className)}>
      <div className="flex items-center justify-between">
        <Text as="p" html={title} className="text-xl font-bold leading-[1.2] text-white" id={sectionId} />
        {isMobile && (
          <button
            onClick={() => setMainNavMenuOpen(!mainNavMenuOpen)}
            aria-expanded={mainNavMenuOpen}
            aria-controls={sectionId}
            aria-label={mainNavMenuOpen ? `Collapse ${title}` : `Expand ${title}`}
            className="focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
          >
            {mainNavMenuOpen ? <SubtractIcon width={20} height={20} /> : <AddIcon width={20} height={20} />}
          </button>
        )}
      </div>
      {((mainNavMenuOpen && isMobile) || !isMobile) && (
        <nav className="mt-4 flex flex-col gap-2" role="region" aria-labelledby={sectionId}>
          {lists?.map((item: any) => (
            <Link href={`${getLocalizedPath(item?.link, locale)}?ref=footer`} key={item?.id} className="no-underline">
              <Text
                html={item?.label}
                className="desktop:text-base text-sm font-bold leading-[1.2] text-[#B6B6BA] hover:text-white"
              />
            </Link>
          ))}
        </nav>
      )}
    </div>
  )
}

/**
 * DownloadApp 下载应用模块
 * @param title
 * @param apps
 * @param className
 * @returns
 */
const DownloadApp = ({ title, apps, className }: { title: string; apps: any[]; className?: string }) => {
  const { locale = 'us' } = useAiuiContext()

  if (!apps?.length) return null
  return (
    <div className={className}>
      <Text html={title} className=" text-xl font-bold leading-[1.2] text-white" />
      <div className="mt-4 flex items-center gap-4" role="list" aria-label="Download apps">
        {apps?.map((appItem: any) => (
          <Link
            key={appItem?.id}
            href={getLocalizedPath(appItem?.link, locale)}
            className="group flex flex-col items-center gap-[6px]"
            role="listitem"
            aria-label={appItem?.icon?.alt || appItem?.label || 'Download app'}
          >
            <img
              src={appItem?.icon?.url}
              loading="lazy"
              alt={appItem?.icon?.alt}
              className="size-10 shrink-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            />
            {appItem?.label && (
              <Text
                html={appItem?.label}
                className="text-xs font-bold text-[#999999] transition-colors duration-200 group-hover:text-white"
              />
            )}
          </Link>
        ))}
      </div>
    </div>
  )
}

/**
 * ChangeCountry 切换国家模块
 * @param data 数据
 * @param isMobile 是否是移动端
 * @returns
 */
const ChangeCountry = ({
  country,
  isMobile,
  className,
  onChangeCountry,
}: {
  country: any
  isMobile: boolean
  className?: string
  onChangeCountry?: () => void
}) => {
  return (
    <div className={cn('desktop:py-0 desktop:border-none border-t border-[#3D3E3F] py-4', className)}>
      <button
        onClick={() => onChangeCountry?.()}
        className="flex w-full items-center justify-between focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
        aria-label={`Change country/region, current: ${country?.countryName || country?.country}`}
      >
        <div className="group flex items-center gap-2">
          <CountryIcon
            width={20}
            height={20}
            className="shrink-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            aria-hidden="true"
          />
          <Text
            as="span"
            html={country?.countryName || country?.country}
            className="desktop:text-[#B6B6BA] desktop:group-hover:text-white text-base font-bold leading-[1.4] text-white transition-colors duration-200"
          />
        </div>
        {isMobile && <ArrowRightIcon width={20} height={20} aria-hidden="true" />}
      </button>
    </div>
  )
}

export default withLayout(FooterNavigation)
